// chat-drawer.plugin.js
// Глобальный drawer переписки HH. Паттерн зеркалит martyrs `popup.auth.plugin.js`:
//   - install(app) кладёт `$openChat(id)` / `$closeChat()` в globalProperties;
//   - host-обёртка рендерится один раз в document.body синхронно и возвращает
//     null пока drawer закрыт — внутренний ChatDrawer (с Popup/useRoute)
//     монтируется только после первого open(), когда роутер уже на app.
import { defineComponent, h, reactive, render, defineAsyncComponent } from 'vue';

// Lazy-загрузка ChatDrawer + ручная prefetch promise на install (см. ниже).
// Без prefetch первый open() ждёт чанк, и `state.visible=true` успевает
// взвестись до того как Popup отрендерится с visible=false — transition
// не ловит false→true edge.
const loadChatDrawer = () => import('@/components/partials/ChatDrawer.vue');
const ChatDrawer = defineAsyncComponent(loadChatDrawer);

const state = reactive({
  visible: false,
  negotiationId: null,
  // Опциональный «черновой» chat, который вызывающий уже знает (карточка
  // из списка). Drawer показывает его сразу — header не моргает плейсхолдером,
  // а лента остаётся в скелетоне до ответа API.
  prefill: null,
  // Callback на нажатие «Назад» внутри ApplicationDialog. Если задан,
  // ChatDrawer вызовет его перед закрытием — это нужно, чтобы вернуть юзера
  // к предыдущему попапу (например, к VacancyPopup, из которого он перешёл).
  onBack: null,
  // После первого open() остаётся true до конца жизни приложения. Нужно чтобы
  // Popup мог проигрывать enter/leave-анимации при последующих open/close —
  // если ремаунтить ChatDrawer на каждый open, его внутренние <transition> без
  // `appear` не успевают отыграть и попап «выскакивает» без анимации.
  mounted: false,
});

function open(negotiationId, prefill = null, options = {}) {
  if (!negotiationId) return;
  state.negotiationId = String(negotiationId);
  state.prefill = prefill && typeof prefill === 'object' ? prefill : null;
  state.onBack = typeof options.onBack === 'function' ? options.onBack : null;

  if (state.mounted) {
    state.visible = true;
    return;
  }

  // Первый open. Чтобы `<transition>` внутри martyrs Popup поймал false→true
  // edge и проиграл enter-анимацию, делаем три вещи:
  //   1) дожидаемся загрузки чанка ChatDrawer (без этого re-render Popup'а
  //      может произойти уже после того как `state.visible` стало true);
  //   2) маунтим хост с visible=false;
  //   3) флипаем visible=true через два rAF — первый кадр даёт Vue время
  //      отрендерить Popup с isPopupOpen=false, второй кадр ловит браузером
  //      paint, и enter-transition стартует с правильной точки.
  loadChatDrawer().then(() => {
    state.mounted = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => { state.visible = true; });
    });
  });
}

function close() {
  state.visible = false;
  state.negotiationId = null;
  state.prefill = null;
  state.onBack = null;
}

// Жмём «Назад» из ApplicationDialog: дёргаем onBack (если задан) и закрываем
// drawer. onBack вызывает open родительского попапа (VacancyPopup и т.п.).
function back() {
  const cb = state.onBack;
  close();
  if (typeof cb === 'function') {
    try { cb(); } catch (_) { /* swallow */ }
  }
}

// Хост-обёртка. До первого open() возвращает null — это критично:
// `Popup` из martyrs внутри своего setup() сразу делает useRoute()/useRouter(),
// и если drawer смонтировать до того, как `app.use(router)` отработал
// (на SSR-client это происходит уже после ModuleCore.initialize), useRoute()
// вернёт undefined и компонент крашнется.
//
// После первого open() ChatDrawer остаётся смонтированным (state.mounted),
// а закрытие/повторное открытие переключает только `state.visible` →
// `<Popup :isPopupOpen>` внутри отыгрывает свои enter/leave-анимации.
const ChatDrawerHost = defineComponent({
  name: 'ChatDrawerHost',
  setup() {
    return () => {
      if (!state.mounted) return null;
      return h(ChatDrawer, { state, close, back });
    };
  },
});

export default {
  install(app) {
    app.config.globalProperties.$openChat = open;
    app.config.globalProperties.$closeChat = close;

    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const container = document.createElement('div');
    container.id = 'chat-drawer-host';
    document.body.appendChild(container);

    const vnode = h(ChatDrawerHost);
    vnode.appContext = app._context;
    render(vnode, container);

    // Prefetch чанка ChatDrawer в idle — первый open() не будет ждать
    // загрузку, два rAF в open() гарантированно поймают false→true edge.
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(() => loadChatDrawer(), { timeout: 2000 });
    } else {
      setTimeout(() => loadChatDrawer(), 0);
    }
  },
};
